// ============================================================
// TMR Portal Admin — leitura da linha do tempo da Empresa, sobre @tmr/api-client
// ============================================================
import { useInfiniteQuery } from '@tanstack/react-query';
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { companyKeys } from '../queryClient';
import type { ActivityCategory, ActivityPage } from './contract';

/** Entries per request. Enough to fill the panel without asking for a page nobody scrolls to. */
export const ACTIVITY_PAGE_SIZE = 25;

/**
 * One Empresa's timeline, paged by cursor (`07` §3).
 *
 * **`useInfiniteQuery` rather than `useQuery`, because the endpoint pages by cursor and not by number.**
 * Entries arrive at the head of the list while it is being read, so an offset would repeat and skip them —
 * the client's job is to keep passing back the opaque `nextCursor` and to append, never to compute a page.
 *
 * `refetchOnWindowFocus` is on, against the portal default (`09` §11, N2). This is one of the queries whose
 * subject moves somewhere else: a contract is signed at the provider, a replication finishes in a worker.
 * Coming back to the tab is exactly the moment something may have happened.
 *
 * @param companyId The Empresa; pass undefined to hold the query while the operator lacks permission.
 * @param category The tab being read, or undefined for "Tudo" — which means every category the operator
 *   may see, not every category that exists.
 */
export function useCompanyActivity(
  companyId: string | undefined,
  category?: ActivityCategory,
): UseInfiniteQueryResult<InfiniteData<ActivityPage>> {
  return useInfiniteQuery({
    queryKey: companyKeys.activity(companyId ?? '', category ?? 'all'),
    queryFn: ({ pageParam, signal }) =>
      api.get<ActivityPage>(`/companies/${companyId}/activity`, {
        query: { category, cursor: pageParam ?? undefined, limit: ACTIVITY_PAGE_SIZE },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(companyId),
    refetchOnWindowFocus: true,
  });
}
